// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GoogleMapsOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import ChartOptionsBuilder from '../Izenda.ChartOptionsBuilder';
import { get, flatMap, getOr, isEmpty, compact, find, groupBy, map, reduce, omit, first, some } from 'lodash/fp';
import {
  applyPointOptionsColor,
  applyValuesColor,
  getTooltipContent,
  getFormattedPointName,
  getNameOfCellConfig
} from '../Izenda.CommonMapOptionsBuilder';
import { DEFAULT_COLOR_PALETTE } from 'logic/data/Izenda.UIConstants';
import tinycolor from 'tinycolor2';
import { updateDisplayStatus } from './Izenda.SimpleLegendControl';

export function composeItemKey(parts: Array) {
  parts = parts || [];
  return parts.reduce((result, part) => result + `${part}#;`, '');
}

export default class GoogleMapsOptionsBuilder extends ChartOptionsBuilder {
  build() {
    const {
      chartOptions: { commonOptions, changeCenterPoint, changeZoomLevel }
    } = this;

    const izendaOptions = get('izendaOptions', commonOptions);
    const seriesMap = getOr({}, 'seriesMap', izendaOptions);

    const mapOptions = this.buildMapOptions();
    const shadingSeries = this.buildSeries('shadingSeries', seriesMap.shadingSeries, 0);
    const bubbleSeries = this.buildSeries(
      'bubbleSeries',
      seriesMap.bubbleSeries,
      isEmpty(shadingSeries.shadingSeries) ? 0 : 1
    );
    const legendOption = this.buildLegendOption(shadingSeries, bubbleSeries);

    return Object.assign({}, mapOptions, shadingSeries, bubbleSeries, legendOption, {
      changeCenterPoint,
      changeZoomLevel
    });
  }

  /**
   * Build map options
   */
  buildMapOptions() {
    const {
      chartOptions: { commonOptions, colors, optionByType, zoomLevel, centerPoint }
    } = this;

    const enabledZoom = getOr(false, 'mapNavigation.enabled', optionByType);
    const align = getOr('right', 'mapNavigation.buttonOptions.align', optionByType);
    const verticalAlign = getOr('bottom', 'mapNavigation.buttonOptions.verticalAlign', optionByType);
    const showMapLabel = getOr(false, 'izendaOptions.mapLabel', optionByType);

    const mapOptions = {
      fullscreenControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      rotateControl: false,
      zoomControl: enabledZoom,
      controlSize: 30,
      gestureHandling: 'greedy',
      zoom: zoomLevel,
      center: centerPoint,
      styles: [
        {
          elementType: 'labels',
          stylers: [{ visibility: showMapLabel ? 'on' : 'off' }]
        }
      ]
    };

    return {
      zoomControlOptions: {
        position: this.getControlPosition(align, verticalAlign)
      },
      mapOptions,
      colors,
      optionByType,
      plotOptions: commonOptions.plotOptions
    };
  }

  /**
   * Get control position
   * @param {String} align
   * @param {String} verticalAlign
   */
  getControlPosition(align: string, verticalAlign: string) {
    if (align === 'izCenter' && verticalAlign === 'izMiddle') {
      return 'CENTER';
    }

    const convertMiddleValueToCenter = value => {
      return value === 'middle' ? 'center' : value;
    };

    const hozAlign = convertMiddleValueToCenter(align);
    const verAlign = convertMiddleValueToCenter(verticalAlign);

    if (verAlign === 'center' && hozAlign === 'center') {
      return 'CENTER';
    }

    return (verAlign === 'top' || (verAlign === 'bottom' && hozAlign === 'center')
      ? `${verAlign}_${hozAlign}`
      : `${hozAlign}_${verAlign}`
    ).toUpperCase();
  }

  /**
   * Add point action to point
   * @param {Object} activePointOption
   * @param {Object} point
   */
  addPointActions(activePointOption, point) {
    const activePointOptionName = get('field.name', activePointOption);
    const pointAction = get('commonActions.pointAction', this.chartOptions);
    const configField = this.fieldOptions[activePointOptionName];

    return configField.numberOfPointActions > 0
      ? function() {
          activePointOption && pointAction(configField, [{ key: activePointOptionName, value: point.pointName }], null);
        }
      : null;
  }

  /**
   * Build series for map
   * @param {String} seriesType
   * @param {Object} seriesObject
   * @param {Number} startColorIndex
   */
  buildSeries(seriesType: String, seriesObject: Object, startColorIndex: Number = 0) {
    const {
      chartOptions: {
        commonOptions,
        colors,
        reportPartState: { activeSerie }
      },
      fieldOptions
    } = this;

    const izendaOptions = get('izendaOptions', commonOptions);
    const seriesMap = getOr({}, 'seriesMap', izendaOptions);
    const activePointOption = getOr({}, 'activePointOption', izendaOptions);
    const pointOption = getOr({}, 'pointOption', seriesMap);

    const series = compact(
      seriesObject
        ? flatMap(
            seriesKey =>
              isEmpty(activeSerie) || activeSerie.name === seriesKey
                ? Object.assign({ name: seriesKey }, seriesObject[seriesKey])
                : null,
            Object.keys(seriesObject)
          )
        : []
    );

    activePointOption && applyPointOptionsColor(series, activePointOption, fieldOptions);
    activePointOption && applyValuesColor(series, activePointOption, fieldOptions);

    const colorPalette = colors || DEFAULT_COLOR_PALETTE;
    const colorPaletteLength = colorPalette.length;

    series.forEach((series: Object, index: Number) => {
      const defaultColor = colorPalette[(index + startColorIndex) % colorPaletteLength];
      series.color = defaultColor;
      series.display = true;
      const configFieldName = get('field.fieldNameAlias', series);
      const cellColorOption = get(`${configFieldName}.cellColors.${activePointOption.pointType}.value`, fieldOptions);

      let highValueColor, lowValueColor;

      if (cellColorOption && seriesType === 'shadingSeries') {
        highValueColor = get('text', find({ name: 'High' }, cellColorOption));
        lowValueColor = get('text', find({ name: 'Low' }, cellColorOption));
      }

      series.data.forEach((point: Object) => {
        const rangesObject = get('rangesObject', point);
        if (get('type', rangesObject) === 'value' && highValueColor && lowValueColor) {
          series.gradientRange = {
            range: rangesObject.range,
            highColor: tinycolor(highValueColor).toHexString(),
            lowColor: tinycolor(lowValueColor).toHexString()
          };
          this.buildGradientRangeColor(point, highValueColor, lowValueColor);
        }
        isEmpty(point.color) && (point.color = defaultColor);
        point.tooltip = getTooltipContent(point, pointOption, fieldOptions, series);
        point.formattedPointName = getFormattedPointName(point, pointOption, fieldOptions, series);
        point.action = this.addPointActions(activePointOption, point);
      });
    });

    const pointType = activePointOption.pointType;

    const allSeries = reduce(
      (result, childSeries) =>
        result.concat(
          this.splitSeriesByColor(childSeries, get(`cellColors.${pointType}`, fieldOptions[childSeries.name]))
        ),
      [],
      series
    );

    return { [seriesType]: allSeries };
  }

  /**
   * Build the legend option
   * @param {Object} param0 The shading series object
   * @param {Object} param1 The bubble series object
   */
  buildLegendOption({ shadingSeries }, { bubbleSeries }) {
    const buildSeries = (series, type) => ({
      label: series.name !== series.metricName ? `${series.name} - ${series.metricName}` : series.name,
      display: !!series.display,
      iconColor: series.color,
      gradientRange: series.gradientRange,
      type,
      eventListeners: [
        {
          eventName: 'click',
          eventHandler: function(map, node) {
            series.display = !series.display;
            const name = get('name', series);
            const metricName = get('metricName', series);
            const nextDisplayStatus = series.display;
            updateDisplayStatus(node, nextDisplayStatus);
            map.refresh({ key: composeItemKey([type, name, metricName]), nextDisplayStatus });
          }
        }
      ]
    });

    const hasDynamicColor = (series) => {
      const pointType = get('activePointOption.pointType', series);
      const fieldOptions = get('field.reportPartElm.properties', series);
      const cellColorOptions = getOr([], `dataFormattings.color.cellColor.${pointType}.value`, fieldOptions);
      return some(x => x.thresholdType === 'dynamic', cellColorOptions);
    };

    const prepareSeries = (data, name) => {
      if (!isEmpty(data)) {
        data = data.filter(series => !isEmpty(series.data) && !hasDynamicColor(series));
        return map(series => buildSeries(series, name), data);
      }
      return [];
    };

    const legendSeries = []
      .concat(prepareSeries(shadingSeries, 'shadingSeries'))
      .concat(prepareSeries(bubbleSeries, 'bubbleSeries'));

    const defaultLegend = get('commonOptions.legend', this.chartOptions);
    const { align: horizontal, verticalAlign: vertical } = defaultLegend;
    const position = this.getControlPosition(horizontal, vertical);

    return { legend: Object.assign({}, defaultLegend, { series: legendSeries, position }) };
  }

  /**
   * Build gradient range color
   * @param {Object} point
   * @param {Object} fieldOptions
   * @param {Object} activePointOption
   */
  buildGradientRangeColor(point: Object, highValueColor: String, lowValueColor: String) {
    // Calculate new color in gradient range
    function calculateColorValue(high, low, delta) {
      return (high - low) * delta + low;
    }

    const highValueColorRgb = tinycolor(highValueColor).toRgb();
    const lowValueColorRgb = tinycolor(lowValueColor).toRgb();

    const value = point.originalValue || point.value;
    const range = get('rangesObject.range[0]', point);

    const highValue = Math.max(...range);
    const lowValue = Math.min(...range);

    if (range[0] !== lowValue) {
      [lowValueColor, highValueColor] = [highValueColor, lowValueColor];
    }

    let pointHexColor;

    if (value <= lowValue) {
      pointHexColor = lowValueColor;
    } else if (value >= highValue) {
      pointHexColor = highValueColor;
    } else {
      let colorDelta = (value - lowValue) / (range[1] - range[0]);

      // Range is reverse
      if (range[0] !== lowValue) {
        colorDelta = 1 - Math.abs(colorDelta);
      }

      pointHexColor = tinycolor({
        r: calculateColorValue(highValueColorRgb.r, lowValueColorRgb.r, colorDelta),
        g: calculateColorValue(highValueColorRgb.g, lowValueColorRgb.g, colorDelta),
        b: calculateColorValue(highValueColorRgb.b, lowValueColorRgb.b, colorDelta)
      }).toHexString();
    }

    !isEmpty(pointHexColor) && (point.color = pointHexColor);
  }

  /**
   * Split current series to many series depend on color config
   * @param {Object} series The series to split
   * @param {Object} cellColorConfig Color config
   */
  splitSeriesByColor(series: Object, cellColorConfig: Object) {
    if (isEmpty(series)) {
      return [series];
    }
    const seriesName = series.name;
    const groupSeries = groupBy(
      point => (get('rangesObject.type', point) === 'value' ? series.color : tinycolor(point.color).toHexString()),
      series.data
    );

    return Object.keys(groupSeries).map(color => {
      const childSeriesName = cellColorConfig ? getNameOfCellConfig(color, cellColorConfig) : null;
      const firstItem = first(groupSeries[color]);
      const name = childSeriesName || (firstItem.pointOptionColor ? firstItem.formattedPointName : seriesName);

      return {
        color,
        name,
        metricName: seriesName,
        data: groupSeries[color],
        ...omit(['data', 'name', 'color'], series)
      };
    });
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/googlemaps/Izenda.GoogleMapsOptionsBuilder.js